
// DefaultPageContent.ts
import type { PageContent } from '../types';
import { industryPagesContentMap } from './industryPagesData';

export const defaultPageContent: Record<string, PageContent> = {
  ...industryPagesContentMap,
  industriesIndex: {
    h1: 'Industry Brand Name Generators',
    body: '<p>Explore our specialized, industry-calibrated brand name generators. Each vertical tool combines domain-specific linguistics, phonetic rules, naming frameworks, and instant domain checks.</p>',
    seo: {
      title: 'Industry Brand Name Generators (30+ Verticals) | BrandGenius',
      description: 'Browse 30+ specialized brand name generators for shoes, coffee, streetwear, luxury, cars, skincare, makeup, beauty, cosmetics, jewelry, fitness, alcohol, and more.',
      canonical: 'https://www.brandgenius.app/industries/',
      slug: '/industries/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  socialMediaAudit: {
    h1: 'Deterministic Social Media Brand Audit',
    body: '<p>Evaluate how effectively your brand presents itself across social channels using our transparent, rules-based consulting methodology.</p>',
    seo: {
      title: 'Instagram & Social Media Brand Audit Tool | BrandGenius',
      description: 'Audit your Instagram brand identity, bio conversion architecture, content strategy, engagement quality, and competitor positioning with deterministic scoring.',
      canonical: 'https://www.brandgenius.app/social-media-audit/',
      slug: '/social-media-audit/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  home: {
    h1: 'The Professional Brand Name Generator & Audit Platform',
    body: `
      <p class="text-lg text-gray-600 mb-8">
        BrandGenius is the all-in-one platform for entrepreneurs, agencies, and visionaries to build world-class brands. 
        Go beyond simple name generation with our suite of professional tools designed to validate, analyze, and perfect your brand identity.
      </p>

      <div class="grid md:grid-cols-2 gap-6 text-left my-8">
        <div class="p-4 bg-gray-50 rounded-lg border border-gray-100">
          <h3 class="font-bold text-gray-900 mb-2">🚀 Intelligent Name Generation</h3>
          <p class="text-sm text-gray-600">Our advanced algorithms combine linguistics, industry trends, and SEO data to generate names that stick.</p>
        </div>
        <div class="p-4 bg-gray-50 rounded-lg border border-gray-100">
          <h3 class="font-bold text-gray-900 mb-2">📊 Consultant-Grade Audits</h3>
          <p class="text-sm text-gray-600">Don't guess—know. Analyze any brand name for phonetic clarity, digital readiness, and structural integrity.</p>
        </div>
        <div class="p-4 bg-gray-50 rounded-lg border border-gray-100">
          <h3 class="font-bold text-gray-900 mb-2">🌐 Instant Domain Checks</h3>
          <p class="text-sm text-gray-600">Verify domain availability in real-time as you generate ideas, saving you hours of manual searching.</p>
        </div>
        <div class="p-4 bg-gray-50 rounded-lg border border-gray-100">
          <h3 class="font-bold text-gray-900 mb-2">📂 Professional PDF Exports</h3>
          <p class="text-sm text-gray-600">Download comprehensive reports and name lists to share with stakeholders, clients, or your team.</p>
        </div>
      </div>

      <p class="text-sm text-gray-500 italic text-center">
        Join over 10,000+ founders who launched their dream brand with BrandGenius.
      </p>
    `,
    seo: {
      title: 'Luxury Fashion & Beauty Brand Name Generator | BrandGenius',
      description: 'Find the perfect name for your fashion, beauty, or luxury brand with BrandGenius. Generate creative, stylish, and memorable names in seconds.',
      canonical: 'https://www.brandgenius.app/',
      slug: '/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  pricing: {
    h1: 'Our Pricing Plans',
    body: '<p>Choose the plan that’s right for you. All plans include full access to our powerful AI Prompt Generators for free.</p>',
    seo: {
      title: 'Pricing Plans | BrandGenius',
      description: 'Explore our free and pro plans. Get unlimited brand name generations, saved favorites, and more with BrandGenius Pro.',
      canonical: 'https://www.brandgenius.app/pricing/',
      slug: '/pricing/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  about: {
    h1: 'About BrandGenius',
    body: '',
    seo: {
      title: 'About BrandGenius | Smart Brand Generator',
      description: 'Learn more about BrandGenius and how it works.',
      canonical: 'https://www.brandgenius.app/about/',
      slug: '/about/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  contact: {
    h1: 'Contact Us',
    body: '',
    seo: {
      title: 'Contact BrandGenius | Smart Brand Generator',
      description: 'Get in touch with the BrandGenius team.',
      canonical: 'https://www.brandgenius.app/contact/',
      slug: '/contact/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  brandAudit: {
    h1: 'Consultant Audit Engine',
    body: '',
    seo: {
      title: 'Brand Name Audit & Analysis Tool | BrandGenius',
      description: 'Analyze your brand name for phonetic clarity, digital readiness, and structural integrity with our professional Brand Audit Engine.',
      canonical: 'https://www.brandgenius.app/brand-audit/',
      slug: '/brand-audit/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },

  // --- AI Prompt Generator Pages ---

  aiPromptGeneratorIndex: {
    h1: 'Free AI Prompt Generator Collection',
    body: '<p>Explore our suite of free, specialized AI prompt generators. Whether you need ideas for art, poetry, fantasy stories, or technical tasks for ChatGPT and Gemini, our tools are designed to spark your creativity and help you get the best results from AI models.</p>',
    seo: {
      title: 'Free AI Prompt Generator Tools | BrandGenius',
      description: 'A collection of free AI prompt generators for art, drawing, poetry, fantasy, ChatGPT, Gemini, and more. Get creative ideas instantly.',
      canonical: 'https://www.brandgenius.app/ai-prompt-generator/',
      slug: '/ai-prompt-generator/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  artPrompts: {
    h1: 'AI Art Prompt Generator',
    body: '<p>Generate creative and professional AI art prompts for Midjourney, DALL·E, and Gemini. Create stunning visuals with detailed prompts tailored to your ideas.</p>',
    seo: {
      title: 'AI Art Prompt Generator (Midjourney, DALL·E) | BrandGenius',
      description: 'Use our free AI Art Prompt Generator to create detailed prompts for image generation tools like Midjourney, DALL·E, and Gemini. Get ideas for styles, subjects, and lighting.',
      canonical: 'https://www.brandgenius.app/ai-prompt-generator/art-prompts/',
      slug: '/ai-prompt-generator/art-prompts/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  drawingPrompts: {
    h1: 'Drawing Prompt Generator',
    body: '<p>Never run out of ideas for your sketches and illustrations. Our generator provides endless creative starting points for artists of all levels.</p>',
    seo: {
      title: 'Random Drawing & Sketch Idea Generator | BrandGenius',
      description: 'Our free drawing prompt generator gives you random ideas for characters, scenes, and concepts. Perfect for beating artist\'s block.',
      canonical: 'https://www.brandgenius.app/ai-prompt-generator/drawing-prompts/',
      slug: '/ai-prompt-generator/drawing-prompts/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  poetryPrompts: {
    h1: 'Poetry Prompt Generator',
    body: '<p>Find inspiration for your next masterpiece. Generate unique prompts for poems, from sonnets to free verse, exploring different themes and emotions.</p>',
    seo: {
      title: 'Creative Poetry Prompt Generator | BrandGenius',
      description: 'Use our poetry prompt generator to get ideas for your next poem. Explore themes, forms, and unique concepts to spark your creativity.',
      canonical: 'https://www.brandgenius.app/ai-prompt-generator/poetry-prompts/',
      slug: '/ai-prompt-generator/poetry-prompts/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  fantasyPrompts: {
    h1: 'Fantasy Writing Prompt Generator',
    body: '<p>Create epic worlds, characters, and plotlines. Our fantasy prompt generator is the perfect tool for writers looking to start their next big story.</p>',
    seo: {
      title: 'Fantasy Writing Prompt & Story Idea Generator | BrandGenius',
      description: 'Generate unique fantasy story ideas, character concepts, and world-building prompts with our free fantasy writing prompt generator.',
      canonical: 'https://www.brandgenius.app/ai-prompt-generator/fantasy-prompts/',
      slug: '/ai-prompt-generator/fantasy-prompts/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  chatgptPrompts: {
    h1: 'ChatGPT Prompt Generator',
    body: '<p>Craft effective prompts to get the best results from ChatGPT. Our tool helps you structure your requests for better summaries, emails, and creative content.</p>',
    seo: {
      title: 'ChatGPT Prompt Generator Tool | BrandGenius',
      description: 'Generate effective and structured prompts for ChatGPT. Get better results for tasks like writing, summarizing, and brainstorming.',
      canonical: 'https://www.brandgenius.app/ai-prompt-generator/chatgpt-prompts/',
      slug: '/ai-prompt-generator/chatgpt-prompts/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  geminiPrompts: {
    h1: 'Google Gemini Prompt Generator',
    body: '<p>Unlock the power of Google\'s Gemini model with prompts designed for multi-modal analysis, code generation, and complex reasoning tasks.</p>',
    seo: {
      title: 'Google Gemini Prompt Generator | BrandGenius',
      description: 'Create advanced prompts for Google Gemini. Generate ideas for coding, data analysis, image-to-text, and other multi-modal tasks.',
      canonical: 'https://www.brandgenius.app/ai-prompt-generator/gemini-prompts/',
      slug: '/ai-prompt-generator/gemini-prompts/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  songPrompts: {
    h1: 'Songwriting & Lyric Prompt Generator',
    body: '<p>Break through writer\'s block and find inspiration for your next song. Generate ideas for lyrics, themes, and song concepts across any genre.</p>',
    seo: {
      title: 'Songwriting & Lyric Idea Prompt Generator | BrandGenius',
      description: 'Get unstuck with our free song prompt generator. Discover ideas for lyrics, song titles, and concepts for your next musical creation.',
      canonical: 'https://www.brandgenius.app/ai-prompt-generator/song-prompts/',
      slug: '/ai-prompt-generator/song-prompts/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  tattooPrompts: {
    h1: 'Tattoo Idea Prompt Generator',
    body: '<p>Design your next piece of body art. Our generator provides unique and meaningful tattoo ideas, combining different styles, subjects, and concepts.</p>',
    seo: {
      title: 'Tattoo Idea & Design Prompt Generator | BrandGenius',
      description: 'Generate unique tattoo ideas and design concepts with our free prompt generator. Combine styles, symbols, and subjects to create your perfect tattoo.',
      canonical: 'https://www.brandgenius.app/ai-prompt-generator/tattoo-prompts/',
      slug: '/ai-prompt-generator/tattoo-prompts/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  funnyAiArtPrompts: {
    h1: 'Funny AI Art Prompt Generator',
    body: '<p>Create hilarious, absurd, and surreal images with AI. This generator is perfect for crafting funny prompts that lead to unexpected and delightful results.</p>',
    seo: {
      title: 'Funny AI Art Prompt Generator | BrandGenius',
      description: 'Generate hilarious and absurd prompts for AI art generators. Create funny images by combining unexpected subjects, settings, and styles.',
      canonical: 'https://www.brandgenius.app/ai-prompt-generator/funny-ai-art-prompts/',
      slug: '/ai-prompt-generator/funny-ai-art-prompts/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  veo3Prompts: {
    h1: 'Veo AI Video Prompt Generator',
    body: '<p>Generate detailed prompts for Google\'s cinematic Veo AI model. Create high-quality video concepts with specifications for style, lighting, and camera movement.</p>',
    seo: {
      title: 'Veo AI Video Prompt Generator | BrandGenius',
      description: 'Craft high-quality, cinematic prompts for Google\'s Veo AI video generator. Specify styles, subjects, and camera shots to get amazing results.',
      canonical: 'https://www.brandgenius.app/ai-prompt-generator/veo3-prompts/',
      slug: '/ai-prompt-generator/veo3-prompts/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  seoPrompts: {
    h1: 'SEO Prompt Generator',
    body: '<p>Free SEO prompt generator for keyword research, blog title ideas, and on-page optimization content.</p>',
    seo: {
      title: 'SEO Prompt Generator for Content & Keywords | BrandGenius',
      description: 'Free SEO prompt generator for keyword research, blog title ideas, and on-page optimization content.',
      canonical: 'https://www.brandgenius.app/ai-prompt-generator/seo-prompts/',
      slug: '/ai-prompt-generator/seo-prompts/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },

  // --- Static & System Pages ---
  dashboard: {
    h1: 'Your Dashboard',
    welcomeMessage: 'Welcome back, {userName}!',
    seo: {
      title: 'Dashboard | BrandGenius',
      description: 'Manage your account, view saved names, and see your generation history.',
      canonical: 'https://www.brandgenius.app/dashboard/',
      slug: '/dashboard/',
      noindex: true,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  help: {
    h1: 'Help & FAQs',
    body: 'Find answers to common questions about BrandGenius.',
    faqs: [
      {
        question: 'How does the brand name generator work?',
        answer: '<p>Our generator uses a sophisticated algorithm combined with a curated database of prefixes, suffixes, and industry keywords to create unique and relevant brand names based on your input.</p>'
      },
      {
        question: 'Is BrandGenius free to use?',
        answer: '<p>Yes, you can use our generator for free up to a certain limit. For unlimited generations and advanced features like saving favorites, you can upgrade to our Pro plan.</p>'
      }
    ],
    seo: {
      title: 'Help & FAQs | BrandGenius',
      description: 'Get help and find answers to frequently asked questions about the BrandGenius brand name generator and our pro plans.',
      canonical: 'https://www.brandgenius.app/help/',
      slug: '/help/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  terms: {
    h1: 'Terms of Service',
    body: '<p>Please read our terms of service carefully before using our application.</p>',
    seo: {
      title: 'Terms of Service | BrandGenius',
      description: 'Read the terms and conditions for using the BrandGenius application and services.',
      canonical: 'https://www.brandgenius.app/terms/',
      slug: '/terms/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
  disclaimer: {
    h1: 'Disclaimer',
    body: '<p>The information provided by BrandGenius is for general informational purposes only. All information on the site is provided in good faith, however we make no representation or warranty of any kind.</p>',
    seo: {
      title: 'Disclaimer | BrandGenius',
      description: 'Read the disclaimer for the BrandGenius application and services.',
      canonical: 'https://www.brandgenius.app/disclaimer/',
      slug: '/disclaimer/',
      noindex: false,
      customHead: '',
      ogImageUrl: '/og-default.jpg',
    },
  },
};
